import React, { useState, useCallback, useRef } from 'react';
import Modal from '../Modal';
import { useCollectionProperties, useModifyCollectionProperties } from '../RealtimeCollectionProvider';
import { CollectionPropertyOption } from '@saga/shared';
import Button from '../styled/Button';
import { Edit2, Trash2, X } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { PopOver, usePopover } from '../popover/PopOver';
import { TaskLabelColors } from '../tasks/LabelSelect';
import { ColorPicker } from '../ColorPickerDropdown';

interface EditPropertyOptionsModalProps {
    isOpen: boolean;
    onClose: () => void;
    collectionId: string;
    propertyId: string;
}

const EditPropertyOptionsModal: React.FC<EditPropertyOptionsModalProps> = ({
    isOpen,
    onClose,
    collectionId,
    propertyId,
}) => {
    const { t } = useTranslation();
    const { properties } = useCollectionProperties(collectionId);
    const { editProperty } = useModifyCollectionProperties(collectionId);

    // Find the property being edited
    const property = React.useMemo(() => {
        return properties?.find((p) => p.id === propertyId);
    }, [properties, propertyId]);

    // State for inline text editing
    const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
    const [editName, setEditName] = useState('');

    // State and refs for color picker popover
    const [editingColorOptionId, setEditingColorOptionId] = useState<string | null>(null);
    const colorTriggerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const modalContentRef = useRef<HTMLDivElement>(null); // Ref for the main modal content area

    // Popover hook for color picker
    const [colorPopoverRef, colorContentRef] = usePopover({
        isOpen: !!editingColorOptionId,
        onClose: () => setEditingColorOptionId(null),
        target: useCallback(() => {
            if (editingColorOptionId && colorTriggerRefs.current[editingColorOptionId]) {
                return colorTriggerRefs.current[editingColorOptionId]?.getBoundingClientRect() ?? null;
            }
            return null;
        }, [editingColorOptionId]),
        // Pass the modal content ref to clickOutsideRefs
        clickOutsideRefs: [modalContentRef],
        position: 'right',
        align: 'center',
    });

    const handleStartEdit = (option: CollectionPropertyOption) => {
        setEditingOptionId(option.id);
        setEditName(option.name);
        setEditingColorOptionId(null); // Close color picker if text edit starts
    };

    const handleCancelEdit = () => {
        setEditingOptionId(null);
        setEditName('');
    };

    const handleSaveEdit = () => {
        if (editingOptionId) {
            editProperty(propertyId, {
                options: property?.options?.map((option) =>
                    option.id === editingOptionId ? { ...option, name: editName } : option,
                ),
            });
        }
        handleCancelEdit();
    };

    const handleDeleteOption = (optionId: string) => {
        if (
            window.confirm(
                t(
                    'collections.properties.options_modal.confirm_delete',
                    'Are you sure you want to delete this option?',
                ),
            )
        ) {
            editProperty(propertyId, {
                options: property?.options?.filter((option) => option.id !== optionId),
            });
        }
    };

    // --- Color Picker Handlers ---
    const handleOpenColorPicker = (optionId: string) => {
        setEditingColorOptionId(optionId);
        setEditingOptionId(null); // Close text edit if color picker opens
    };

    const handleSelectColor = (color: string) => {
        if (editingColorOptionId) {
            editProperty(propertyId, {
                options: property?.options?.map((option) =>
                    option.id === editingColorOptionId ? { ...option, color } : option,
                ),
            });
        }
        setEditingColorOptionId(null); // Close picker after selection
    };

    if (!isOpen || !property) {
        return null;
    }

    // Ensure options are an array
    const options: CollectionPropertyOption[] = property.options || [];

    return (
        <>
            <Modal.Small isOpen={isOpen} onClose={onClose}>
                <div className="p-5 bg-white dark:bg-saga-gray-900 rounded-lg" ref={modalContentRef}>
                    <div className="flex justify-between items-center mb-4 pr-1">
                        <h3 className="text-lg font-semibold">
                            {t('collections.properties.options_modal.title', { propertyName: property.title })}
                        </h3>
                        <Button.Plain onClick={onClose} aria-label={t('common.close', 'Close')}>
                            <span className="p-1 block rounded hover:bg-saga-gray-100 dark:hover:bg-saga-gray-800">
                                <X size={20} />
                            </span>
                        </Button.Plain>
                    </div>

                    <div className="space-y-2 max-h-80 overflow-y-auto">
                        {options.map((option) => (
                            <div
                                key={option.id}
                                className="flex items-center justify-between px-2 py-1 rounded hover:bg-saga-gray-100 dark:hover:bg-saga-gray-800 min-h-10 group"
                            >
                                {/* --- Left Group --- */}
                                <div className="flex items-center space-x-2 flex-grow min-w-0 pr-2">
                                    {/* Color Dot */}
                                    <div
                                        ref={(el) => (colorTriggerRefs.current[option.id] = el)}
                                        className="w-3 h-3 rounded-full flex-shrink-0 cursor-pointer ring-1 ring-transparent hover:ring-saga-gray-400 dark:hover:ring-saga-gray-500"
                                        style={{ backgroundColor: option.color }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenColorPicker(option.id);
                                        }}
                                        aria-label={t(
                                            'collections.properties.options_modal.change_color',
                                            'Change color',
                                        )}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleOpenColorPicker(option.id);
                                            }
                                        }}
                                    />

                                    {editingOptionId === option.id ? (
                                        // --- Edit Mode Content ---
                                        <>
                                            {/* Input */}
                                            <input
                                                type="text"
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                className="flex-grow min-w-0 bg-transparent border border-saga-gray-300 dark:border-saga-gray-600 rounded px-1 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault(); // Prevent potential form submission
                                                        handleSaveEdit();
                                                    }
                                                    if (e.key === 'Escape') {
                                                        e.stopPropagation(); // Stop event from bubbling to Modal
                                                        handleCancelEdit();
                                                    }
                                                }}
                                            />
                                            {/* Save Button */}
                                            <Button.Plain onClick={handleSaveEdit} data-testid={`save-${option.id}`}>
                                                <span className="text-xs px-1.5 py-0.5 rounded text-saga-gray-700 dark:text-saga-gray-300 hover:bg-saga-gray-200 dark:hover:bg-saga-gray-700">
                                                    {t('common.save', 'Save')}
                                                </span>
                                            </Button.Plain>
                                            {/* Cancel Button */}
                                            <Button.Plain
                                                onClick={handleCancelEdit}
                                                data-testid={`cancel-${option.id}`}
                                            >
                                                <span className="text-xs px-1.5 py-0.5 rounded hover:bg-saga-gray-200 dark:hover:bg-saga-gray-700">
                                                    {t('common.cancel', 'Cancel')}
                                                </span>
                                            </Button.Plain>
                                        </>
                                    ) : (
                                        // --- Display Mode Content ---
                                        <>
                                            {/* Display Name */}
                                            <span
                                                className="text-sm flex items-center h-6 truncate"
                                                title={option.name}
                                            >
                                                {option.name}
                                            </span>
                                            {/* Edit Button */}
                                            <Button.Plain
                                                onClick={() => handleStartEdit(option)}
                                                aria-label={t('common.edit', 'Edit')}
                                                data-testid={`edit-${option.id}`}
                                            >
                                                <span className="block p-1 rounded hover:bg-saga-gray-200 dark:hover:bg-saga-gray-700">
                                                    <Edit2 size={14} />
                                                </span>
                                            </Button.Plain>
                                        </>
                                    )}
                                </div>

                                {/* --- Right Group (Delete Button) --- */}
                                <div className="flex">
                                    {editingOptionId !== option.id && (
                                        <Button.Plain
                                            onClick={() => handleDeleteOption(option.id)}
                                            aria-label={t('common.delete', 'Delete')}
                                            data-testid={`delete-${option.id}`}
                                        >
                                            <span className="block p-1 rounded text-saga-red hover:bg-saga-red/10">
                                                <Trash2 size={14} />
                                            </span>
                                        </Button.Plain>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal.Small>

            {/* Color Picker Popover */}
            <PopOver isOpen={!!editingColorOptionId} ref={colorPopoverRef} contentRef={colorContentRef} zIndex={400}>
                <div
                    className="p-1.5 shadow-popupSmall rounded-md bg-white dark:bg-saga-gray-900"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()} // Also stop keyboard events
                >
                    <ColorPicker
                        availableColors={TaskLabelColors}
                        selectedColor={options.find((opt) => opt.id === editingColorOptionId)?.color || ''}
                        onSelectColor={handleSelectColor}
                    />
                </div>
            </PopOver>
        </>
    );
};

export default EditPropertyOptionsModal;
